import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog.jsx';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { api } from '@/lib/api.js';
import CrmContactPicker from './CrmContactPicker.jsx';
import { exportLicenses } from '@/services/licenses.service.js';

// Export the license footprint to a carrier contact. PDF goes out as a
// WorkDrive permalink + the full plain-text table inline (sendCRMEmail can't
// attach). "Download only" produces the PDF + text without emailing.
export default function CarrierExportDialog({ open, onOpenChange, me }) {
  const [carriers, setCarriers] = useState([]);
  const [agency, setAgency] = useState(true);
  const [includeProducer, setIncludeProducer] = useState(false);
  const [broker, setBroker] = useState(null);
  const [statesInput, setStatesInput] = useState('');
  const [carrierSlug, setCarrierSlug] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [fmtPdf, setFmtPdf] = useState(true);
  const [fmtText, setFmtText] = useState(true);
  const [subject, setSubject] = useState('NDEMNIFLY license footprint, current');
  const [intro, setIntro] = useState('Current license footprint attached for producer-code assignment. PDF linked, plain text below for copy-paste.');
  const [preview, setPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/api/admin/carriers?_t=${Date.now()}`).then((d) => setCarriers(Array.isArray(d) ? d : [])).catch(() => setCarriers([]));
  }, [open]);

  const selfBroker = me?.broker_contact_id ? { id: me.broker_contact_id, name: me.broker_name } : null;
  const effectiveBroker = me?.is_admin ? (broker || selfBroker) : selfBroker;
  const carrier = carriers.find((c) => c.slug === carrierSlug);
  const contacts = Array.isArray(carrier?.contacts) ? carrier.contacts : [];

  function buildBody(mode) {
    const states = statesInput.trim()
      ? statesInput.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean)
      : null;
    const format = [fmtPdf && 'pdf', fmtText && 'plain_text'].filter(Boolean);
    return {
      scope: {
        agency,
        producer_broker_contact_id: includeProducer ? effectiveBroker?.id : undefined,
        states,
      },
      format: format.length ? format : ['plain_text'],
      delivery: mode === 'send_email'
        ? { mode, carrier_slug: carrierSlug, contact_email: contactEmail || undefined, subject, intro }
        : { mode: 'download_only' },
    };
  }

  const scopeOk = agency || (includeProducer && effectiveBroker?.id);

  async function refreshPreview() {
    if (!scopeOk) return;
    setBusy(true);
    try {
      const res = await exportLicenses(buildBody('download_only'));
      setPreview(res.plain_text_body || '');
      if (res.pdf_url && fmtPdf) toast.success('PDF generated to WorkDrive');
    } catch (err) { toast.error(`Preview failed: ${err.body?.error || err.message}`); }
    finally { setBusy(false); }
  }

  async function doSend() {
    setBusy(true);
    try {
      const res = await exportLicenses(buildBody('send_email'));
      toast.success(res.sent ? 'License list sent' : 'Generated');
      for (const w of (Array.isArray(res?.warnings) ? res.warnings : [])) toast.info(w);
      setConfirmOpen(false); onOpenChange(false);
    } catch (err) { toast.error(`Send failed: ${err.body?.error || err.message}`); }
    finally { setBusy(false); }
  }

  const recipient = contacts.find((c) => (c.email || '') === contactEmail) || contacts.find((c) => c.default) || contacts[0];
  const canSend = scopeOk && carrierSlug && (recipient?.email);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Export license list to carrier</DialogTitle>
            <DialogDescription>PDF goes out as a WorkDrive link + the plain-text table inline. Internal notes are never included.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4">
            {/* Scope */}
            <fieldset className="grid gap-2 rounded-card border border-border p-3">
              <legend className="px-1 text-xs font-medium text-text-muted">Scope</legend>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={agency} onChange={(e) => setAgency(e.target.checked)} /> Agency licenses</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={includeProducer} onChange={(e) => setIncludeProducer(e.target.checked)} /> Producer licenses{!me?.is_admin && ' (mine)'}</label>
              {includeProducer && me?.is_admin && <CrmContactPicker value={broker} onSelect={setBroker} placeholder="Broker (defaults to you)…" />}
              <div>
                <label className="mb-1 block text-[11px] uppercase tracking-wide text-text-dim">Limit to states (optional, comma-separated)</label>
                <Input placeholder="GA, LA, MS — blank = all" value={statesInput} onChange={(e) => setStatesInput(e.target.value)} />
              </div>
            </fieldset>

            {/* Recipient */}
            <fieldset className="grid gap-2 rounded-card border border-border p-3">
              <legend className="px-1 text-xs font-medium text-text-muted">Recipient</legend>
              <select className="h-11 rounded-card border border-border bg-surface px-3 text-sm" value={carrierSlug} onChange={(e) => { setCarrierSlug(e.target.value); setContactEmail(''); }}>
                <option value="">Select carrier…</option>
                {carriers.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
              {carrier && (
                <select className="h-11 rounded-card border border-border bg-surface px-3 text-sm" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}>
                  <option value="">{contacts.length ? 'Default contact' : 'No contacts on file'}</option>
                  {contacts.map((c, i) => <option key={i} value={c.email}>{c.name} — {c.email}{c.default ? ' (default)' : ''}</option>)}
                </select>
              )}
            </fieldset>

            {/* Format + message */}
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={fmtPdf} onChange={(e) => setFmtPdf(e.target.checked)} /> PDF link</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={fmtText} onChange={(e) => setFmtText(e.target.checked)} /> Plain-text body</label>
            </div>
            <div><label className="mb-1 block text-xs font-medium text-text-muted">Subject</label><Input value={subject} onChange={(e) => setSubject(e.target.value)} /></div>
            <div><label className="mb-1 block text-xs font-medium text-text-muted">Intro</label><textarea className="min-h-[64px] w-full rounded-card border border-border bg-surface p-2 text-sm" value={intro} onChange={(e) => setIntro(e.target.value)} /></div>

            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-xs font-medium text-text-muted">Plain-text preview</label>
                <button type="button" onClick={refreshPreview} disabled={!scopeOk || busy} className="text-xs text-accent hover:underline disabled:opacity-50">Refresh preview</button>
              </div>
              <pre className="max-h-48 overflow-auto rounded-card border border-border bg-surface p-3 text-[11px] leading-snug text-text-muted">{preview || 'Click “Refresh preview”.'}</pre>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button variant="outline" disabled={!scopeOk || busy} onClick={refreshPreview}>Download only</Button>
            <Button disabled={!canSend || busy} onClick={() => setConfirmOpen(true)}>Send…</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Email {recipient?.name || 'this contact'}?</AlertDialogTitle>
            <AlertDialogDescription>
              This sends the license footprint to <strong>{recipient?.email}</strong> at {carrier?.name}. This is an external email.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Back</AlertDialogCancel>
            <AlertDialogAction onClick={doSend} disabled={busy}>{busy ? 'Sending…' : 'Send'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
